import { Component, Input, NgZone, OnDestroy } from "@angular/core";
import moment from "moment-timezone";
import { Subscription } from "rxjs";
import { ImpersonationService } from "../../../../auth/apigee/impersonation.service";
import { LoggerService } from "../../../../common/logging/logger.service";
import { ModalDialogComponent } from "../../../../common/modal/modalDialog.component";
import { OfficeSelectorService } from "../../../../common/officeSelector/officeSelector.service";
import { VisitDetailData, VisitsCallsTargetsData, VisitsCallsTargetsService, VisitsCallsTargetsServiceData } from "../../services/visitsCallsTargetsKpi/visitsCallsTargets.service";
import { BarChartData, BarChartDataset } from "../../../../dashboardCommon/components/cardContents/barChart.component";
import { ChartDropdownOption } from "../../../../dashboardCommon/components/cardContents/chartDropdown.component";
import { ColumnDefinition } from "../../../../dashboardCommon/components/cardDetails/datatable.component";
import { SingleSummaryDetailKpiServiceHost } from "../../../../dashboardCommon/components/kpiServiceHostComponent";

export class VisitsCallsEvolutionData {
    public barChartData: BarChartData;
    public visitCount: number;
    public visitTarget: number;


    constructor(barChartData: BarChartData, visitCount: number, visitTarget:number) {
        this.barChartData = barChartData;
        this.visitCount = visitCount;
        this.visitTarget = visitTarget;
    }
}

export class VisitsCallsEvolutionDetailData {
    public columnDefinitions: ColumnDefinition[];
    public tabledata: any[][];
}

@Component({
    selector: 'visits-calls-evolution-kpi-service-host',
    template: '<ng-content></ng-content>',
    standalone: false
})
export class VisitsCallsEvolutionKpiServiceHostComponent extends SingleSummaryDetailKpiServiceHost<VisitsCallsTargetsService, VisitsCallsTargetsServiceData, ChartDropdownOption[], VisitsCallsEvolutionDetailData>
    implements OnDestroy {
    public getClassName() { return "VisitsCallsEvolutionKpiServiceHostComponent"; }

    private raasSubscription: Subscription;
    private officeSelectorSubscription: Subscription;
    @Input() public detailDialog: ModalDialogComponent;
    @Input() public messageDialog: ModalDialogComponent;

    constructor(zone: NgZone, logger: LoggerService, private impersonationService: ImpersonationService, private officeSelectorService: OfficeSelectorService,
        summaryService: VisitsCallsTargetsService) {
        super(zone, logger, summaryService);
        this.raasSubscription = this.impersonationService.impersonationChanged.subscribe(() => { this.resetSummary(); });
        this.officeSelectorSubscription = this.officeSelectorService.selectedOfficeUpdated.subscribe(() => { this.resetSummary(); });
    }

    protected processSummaryData(data: VisitsCallsTargetsServiceData, updateRequested: Boolean): ChartDropdownOption[] {
        if (data.entries && data.entries.length > 0) {
            let options: ChartDropdownOption[] = null;
            let globalOption = this.generateGlobal(data.entries);
            if (globalOption) {
                options = [globalOption];
                options = options.concat(data.entries.map<ChartDropdownOption>(x => this.generateOptionData(x)).filter(x => x != null)
                    .sort((a, b) => (a.text ? a.text.toLowerCase() : "") < (b.text ? b.text.toLowerCase() : "") ? -1 : 1));
                let now = moment();
                let diff = moment.duration(now.diff(moment(data.officeVisitsUpdateTimestamp)));
                if (diff.hours() < 2 && this.summaryData && updateRequested) {
                    this.messageDialog.open();
                }

                return options;
            } else {
                return this.getDefaultSummaryData();
            }

        } else {
            return this.getDefaultSummaryData();
        }
    }

    protected getDefaultSummaryData(): ChartDropdownOption[] {
        let global = new VisitsCallsTargetsData();
        global.name = "Todos";
        global.visits = [];
        global.calls = [];
        global.hangouts = [];
        global.visitsTarget = 0;


        return [this.generateOptionData(global)];
    }

    private generateGlobal(entries: VisitsCallsTargetsData[]): ChartDropdownOption {
        let global = new VisitsCallsTargetsData();

        let visitTargetArray = [];
        entries.forEach(function (element) {
            visitTargetArray.push(element.visitsTarget);
        });

        global.name = "Todos";
        global.visits = entries.reduce<VisitDetailData[]>((acc, x) => acc.concat(x.visits || []), []);
        global.visitsTarget = visitTargetArray.reduce(function (a, b) { return a + b; })

        return this.generateOptionData(global);
    }

    private generateOptionData(entry: VisitsCallsTargetsData): ChartDropdownOption {
        let now = moment();
        let currentWeek = now.isoWeek();

        let visitsWeek: number[] = new Array<number>(5); visitsWeek.fill(0, 0, 5);
        if (entry.visits) {
            entry.visits.filter(x => moment(x.date).isoWeek() == currentWeek)
                .reduce<number[]>((acc, x) => {
                    let weekDay = moment(x.date).isoWeekday() - 1;
                    if (weekDay < 5) {
                        acc[weekDay]++;
                    }
                    return acc;
                }, visitsWeek);
        }

        let option = new ChartDropdownOption();
        option.text = entry.name;
        let visitsCount = visitsWeek.reduce<number>((acc, x) => acc + x, 0);
        let visitsTarget = entry.visitsTarget || 0;
        option.value = new VisitsCallsEvolutionData(new BarChartData(
            ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes"],
            [
                new BarChartDataset("Visitas ({0}/{1})".format(visitsCount.toString(), visitsTarget.toString()), visitsWeek),
            ]),
            visitsCount,
            visitsTarget
        );

        return option;
    }

    protected extractUpdateTime(data: VisitsCallsTargetsServiceData): Date {
        const MIN_DATE = moment("1970-01-01");
        let date = MIN_DATE;

        let test = moment().toISOString();
        let now = moment(test).tz("Etc/GMT");

        data.entries.forEach(x => {
            let aas = moment(x.callsUpdateTimestamp.toString());
            if (x.callsUpdateTimestamp) date = moment.max(date, moment(x.callsUpdateTimestamp.toString()));
            if (x.visitsUpdateTimestamp) date = moment.max(date, moment(x.visitsUpdateTimestamp.toString()));
            if (x.visitsTargetUpdateTimestamp) date = moment.max(date, moment(x.visitsTargetUpdateTimestamp.toString()));
            if (x.hangoutsUpdateTimestamp) date = moment.max(date, moment(x.hangoutsUpdateTimestamp.toString()));
        });

        if (date == MIN_DATE) return null;
        else {
            return new Date(date.toString());
        }
    }

    protected processDetailData(data: VisitsCallsTargetsServiceData): VisitsCallsEvolutionDetailData {
        return new VisitsCallsEvolutionDetailData();
    }

    protected getDefaultDetailData(): VisitsCallsEvolutionDetailData {
        return new VisitsCallsEvolutionDetailData();
    }

    protected performDetailAction(): void {
        if (this.detailDialog) this.detailDialog.open();
    }

    public ngOnDestroy(): void {
        if (this.raasSubscription) this.raasSubscription.unsubscribe();
        if (this.officeSelectorSubscription) this.officeSelectorSubscription.unsubscribe();
    }
}
